// Types only - chart.js itself is loaded lazily with TrackChart
import type { ChartDataset, ScriptableContext } from "chart.js";
import type {
  ChartDataPoint,
  XAxisTooltipFormatter,
  YAxisTooltipFormatter,
} from "components/UI/Chart/TrackChart";
import { UnitType } from "types/app";
import { getElevationGrade } from "utils/elevations";
import { formatDistance } from "utils/formatters/formatDistance";
import { formatElevation } from "utils/formatters/formatElevation";
import { formatSpeed } from "utils/formatters/formatSpeed";
import { formatTemperature } from "utils/formatters/formatTemperature";
import { getSecondsAsHMS } from "utils/helpers";
import { CustomChartDataPoint } from "utils/trackStats/prepareTrackStats";

/** What the chart shows on the vertical axis: one of the track's datasets */
export type ChartYAxisType = keyof CustomChartDataPoint["yAxis"];
/** What the chart shows on the horizontal axis */
export type ChartXAxisType = "distance" | "time";

type YAxis = {
  color: string;
  /** Only the elevation fills the area under its line */
  fill?: boolean;
  /** A value of the axis, `decimals` where the unit has them */
  format: (value: number, units: UnitType, decimals: number) => string;
  /** Decimals of the hovered value (the axis labels have none) */
  tooltipDecimals: number;
};

export const Y_AXES: Record<ChartYAxisType, YAxis> = {
  elevation: {
    color: "rgba(54, 162, 235, 1)",
    fill: true,
    format: formatElevation,
    tooltipDecimals: 1,
  },
  speed: {
    color: "rgba(236, 157, 20, 1)",
    format: formatSpeed,
    tooltipDecimals: 0,
  },
  cadence: {
    color: "rgba(72, 110, 233, 1)",
    format: (value) => `${value} rpm`,
    tooltipDecimals: 0,
  },
  heartRate: {
    color: "rgba(244, 52, 138, 1)",
    format: (value) => `${value} bpm`,
    tooltipDecimals: 0,
  },
  power: {
    color: "rgba(171, 120, 255, 1)",
    format: (value) => `${value} W`,
    tooltipDecimals: 0,
  },
  temperature: {
    color: "rgba(245, 71, 71, 1)",
    format: formatTemperature,
    tooltipDecimals: 0,
  },
};

export const isChartYAxisType = (
  value: string | null
): value is ChartYAxisType => value !== null && value in Y_AXES;

/** The datasets the track has data for, in the order of `Y_AXES` */
export const getPopulatedYAxes = (
  populatedDatasets: Partial<Record<string, boolean>>
): ChartYAxisType[] =>
  (Object.keys(Y_AXES) as ChartYAxisType[]).filter(
    (type) => populatedDatasets[type] === true
  );

/** The points of a dataset: distance (km) or time (s) from the start, and the value (null in gaps of the data) */
export const getChartPoints = (
  chartData: CustomChartDataPoint[],
  yAxis: ChartYAxisType,
  xAxis: ChartXAxisType
): ChartDataPoint[] =>
  chartData.map(({ xAxis: x, yAxis: y }) => ({
    x: xAxis === "distance" ? x.distance : (x.time ?? 0),
    y: y[yAxis],
  }));

// Top and bottom opacity of the area under the chart line
const GRADIENT_FILL_OPACITY = { top: 0.6, bottom: 0.05 };

const withOpacity = (rgbaColor: string, opacity: number) =>
  rgbaColor.replace(/[\d.]+\)$/, `${opacity})`);

/** Fill fading from the line color at the top of the chart to fully transparent at the bottom */
const getGradientFill =
  (strokeColor: string) =>
  ({ chart }: ScriptableContext<"line">) => {
    const { ctx, chartArea } = chart;
    const topColor = withOpacity(strokeColor, GRADIENT_FILL_OPACITY.top);

    // The chart area isn't known until the chart is laid out for the first time
    if (!chartArea) return topColor;

    const gradient = ctx.createLinearGradient(
      0,
      chartArea.top,
      0,
      chartArea.bottom
    );
    gradient.addColorStop(0, topColor);
    gradient.addColorStop(
      1,
      withOpacity(strokeColor, GRADIENT_FILL_OPACITY.bottom)
    );
    return gradient;
  };

const COMMON_DATASET_PROPERTIES: Partial<
  ChartDataset<"line", ChartDataPoint[]>
> = {
  pointBorderColor: "rgba(0, 0, 0, 0)",
  pointBackgroundColor: "rgba(0, 0, 0, 0)",
  pointHoverRadius: 10,
  pointHitRadius: 20,
  borderWidth: 2,
  // Smooth the line without overshooting real min/max values
  cubicInterpolationMode: "monotone",
  borderJoinStyle: "round",
  borderCapStyle: "round",
};

/** The chart.js dataset of the chosen data */
export const getDataset = (
  chartData: CustomChartDataPoint[],
  yAxis: ChartYAxisType,
  xAxis: ChartXAxisType
): ChartDataset<"line", ChartDataPoint[]> => {
  const { color, fill } = Y_AXES[yAxis];
  return {
    ...COMMON_DATASET_PROPERTIES,
    data: getChartPoints(chartData, yAxis, xAxis),
    // The recorded data has gaps (e.g. no heart rate for a while), the line isn't drawn through them
    ...(fill ? { fill: true } : { spanGaps: false }),
    backgroundColor: getGradientFill(color),
    borderColor: color,
  };
};

/** The labels of the horizontal axis and its hovered value */
export const formatXAxisValue = (
  xAxis: ChartXAxisType,
  value: number,
  units: UnitType,
  decimals: number
) =>
  xAxis === "distance"
    ? formatDistance(value * 1000, units, decimals)
    : getSecondsAsHMS(value);

export const getXAxisTooltipFormatter =
  (xAxis: ChartXAxisType, units: UnitType): XAxisTooltipFormatter =>
  (value) =>
    value === null ? "N/A" : formatXAxisValue(xAxis, value, units, 3);

/**
 * The hovered value, with the grade from the previous point for the elevation over the distance
 * (the grade is the rise over the distance, not over the time)
 */
export const getYAxisTooltipFormatter =
  (
    yAxis: ChartYAxisType,
    xAxis: ChartXAxisType,
    units: UnitType
  ): YAxisTooltipFormatter =>
  ({ prevPoint, currentPoint }) => {
    // Points in gaps of the data (e.g. no heart rate recorded) have no value
    if (currentPoint.y === null || currentPoint.y === undefined) return "N/A";

    const { format, tooltipDecimals } = Y_AXES[yAxis];
    const value = format(currentPoint.y, units, tooltipDecimals);
    if (yAxis !== "elevation" || xAxis !== "distance" || !prevPoint) {
      return value;
    }

    const grade = getElevationGrade(currentPoint, prevPoint)?.toFixed(1);
    return grade ? `${value} (${grade}%)` : value;
  };
